import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Empty,
  Form,
  Grid,
  Input,
  List,
  Modal,
  Radio,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  GlobalOutlined,
  LockOutlined,
  PlusOutlined,
  ShopOutlined,
  SwapOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useBlocker } from 'react-router-dom';
import dayjs from 'dayjs';
import { ModuleIcon } from '@shared/common/components/ui/module-launcher';
import {
  createModule,
  getModuleHistory,
  updateModule,
  type StudioModule,
  type StudioModuleCatalog,
  type StudioModuleChange,
  type StudioModuleProblem,
} from '@/api/studioModules';
import { destinationByKey, moduleGlyph, MODULE_ICONS } from '@/layouts/navigation';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import {
  describeChange,
  draftFrom,
  holdsRecoveryRoute,
  impactOf,
  mergeModule,
  slugify,
  titleOf,
  type DraftItem,
  type MergeChoice,
  type ModuleDraft,
  type ModuleMerge,
} from './moduleEditing';

const { Text, Paragraph } = Typography;

interface Props {
  open: boolean;
  /** null = create a new module. */
  module: StudioModule | null;
  catalog: StudioModuleCatalog;
  onClose: () => void;
  /** Called after a successful save; the page reloads the catalog and the navigation. */
  onSaved: () => Promise<void> | void;
  /** Loads the latest catalog (for the conflict view). */
  reloadCatalog: () => Promise<StudioModuleCatalog | null>;
}

const FIELD_NAMES = new Set(['moduleKey', 'title', 'description', 'iconKey', 'color']);

/** A merge waiting on the person's choices: the three sides it was built from, and what they chose so far. */
interface PendingMerge {
  original: StudioModule | null;
  yours: ModuleDraft;
  latest: StudioModule | null;
  catalog: StudioModuleCatalog;
  choices: Record<string, MergeChoice>;
}

export const ModuleEditorDrawer = observer(function ModuleEditorDrawer({ open, module, catalog, onClose, onSaved, reloadCatalog }: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const screens = Grid.useBreakpoint();
  const creating = module == null;

  const [base, setBase] = useState<StudioModule | null>(module);
  const [baseCatalog, setBaseCatalog] = useState<StudioModuleCatalog>(catalog);
  const [draft, setDraft] = useState<ModuleDraft>(() => draftFrom(module));
  const [keyTouched, setKeyTouched] = useState(false);
  const [problems, setProblems] = useState<StudioModuleProblem[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [conflict, setConflict] = useState<{ message: string; current: StudioModule | null } | null>(null);
  const [mergeNotes, setMergeNotes] = useState<string[]>([]);
  const [pendingMerge, setPendingMerge] = useState<PendingMerge | null>(null);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [tab, setTab] = useState('details');

  // Fresh state each time the drawer opens on a module (or on "new").
  useEffect(() => {
    if (!open) return;
    setBase(module);
    setBaseCatalog(catalog);
    setDraft(draftFrom(module));
    setKeyTouched(false);
    setProblems([]);
    setFieldErrors({});
    setConflict(null);
    setMergeNotes([]);
    setPendingMerge(null);
    setTab('details');
    // Only re-seed when a different module is opened, not on every catalog refresh behind an open drawer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, module?.id]);

  const pristine = useMemo(() => JSON.stringify(draftFrom(base)), [base]);
  const dirty = open && JSON.stringify(draft) !== pristine;

  // Leaving the page with unsaved edits (rail, back button, a module tile) asks first.
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    Modal.confirm({
      title: 'Leave without saving?',
      content: 'Your changes to this module are not saved.',
      okText: 'Leave',
      okButtonProps: { danger: true },
      cancelText: 'Keep editing',
      onOk: () => blocker.proceed?.(),
      onCancel: () => blocker.reset?.(),
    });
  }, [blocker]);

  const requestClose = (): void => {
    if (!dirty) return onClose();
    Modal.confirm({
      title: 'Discard changes?',
      content: 'Your changes to this module are not saved.',
      okText: 'Discard',
      okButtonProps: { danger: true },
      cancelText: 'Keep editing',
      onOk: onClose,
    });
  };

  const set = <K extends keyof ModuleDraft>(key: K, value: ModuleDraft[K]): void => {
    setDraft((d) => ({ ...d, [key]: value }));
    if (fieldErrors[key as string]) setFieldErrors((e) => ({ ...e, [key as string]: '' }));
  };

  const move = (index: number, delta: number): void => {
    setDraft((d) => {
      const items = [...d.items];
      const to = index + delta;
      if (to < 0 || to >= items.length) return d;
      [items[index], items[to]] = [items[to], items[index]];
      return { ...d, items };
    });
  };

  const removeAdded = (navKey: string): void => {
    setDraft((d) => ({ ...d, items: d.items.filter((i) => i.navKey !== navKey), defaultNavKey: d.defaultNavKey === navKey ? null : d.defaultNavKey }));
  };

  const addItems = (keys: string[]): void => {
    const owners = new Map(baseCatalog.destinations.map((d) => [d.navKey, d]));
    setDraft((d) => ({
      ...d,
      items: [
        ...d.items,
        ...keys
          .filter((k) => !d.items.some((i) => i.navKey === k))
          .map<DraftItem>((k) => ({ navKey: k, rowVersion: owners.get(k)?.itemRowVersion ?? null, fromModuleKey: owners.get(k)?.ownerModuleKey ?? null })),
      ],
    }));
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    setProblems([]);
    setFieldErrors({});
    try {
      const body = {
        moduleKey: creating ? draft.moduleKey.trim() : undefined,
        title: draft.title,
        description: draft.description || null,
        iconKey: draft.iconKey,
        color: draft.color || null,
        defaultNavKey: draft.defaultNavKey,
        isVisible: draft.isVisible,
        rowVersion: base?.rowVersion ?? null,
        items: draft.items.map((i) => ({ navKey: i.navKey, rowVersion: i.rowVersion })),
      };
      const result = creating ? await createModule(body) : await updateModule(base!.id, body);
      switch (result.kind) {
        case 'saved':
          void message.success(creating ? `Created "${draft.title.trim()}".` : `Saved "${draft.title.trim()}".`);
          setBase(result.module);
          await onSaved();
          onClose();
          return;
        case 'invalid': {
          const byField: Record<string, string> = {};
          const general: StudioModuleProblem[] = [];
          for (const p of result.problems) {
            if (p.field && FIELD_NAMES.has(p.field)) byField[p.field] = p.message;
            else general.push(p);
          }
          setFieldErrors(byField);
          setProblems(general);
          return;
        }
        case 'conflict':
          setConflict({ message: result.message, current: result.current });
          return;
        default:
          setProblems([{ code: result.kind, message: result.message }]);
      }
    } catch (err) {
      setProblems([{ code: 'error', message: (err as Error).message }]);
    } finally {
      setSaving(false);
    }
  };

  /** The merged draft becomes the edit, on top of the module as it is now. */
  const applyMerge = (merged: ModuleMerge, latest: StudioModule | null): void => {
    if (!creating) setBase(latest);
    setDraft(merged.draft);
    setMergeNotes(merged.notes);
    setPendingMerge(null);
  };

  /**
   * Conflict → reload the latest catalog and merge three ways: the module as this editor opened it, this edit, and the
   * module as saved now. Changes only one side made go through; a field or page both sides changed waits for a choice.
   */
  const mergeWithLatest = async (): Promise<void> => {
    const latestCatalog = await reloadCatalog();
    if (!latestCatalog) return;
    const latest = creating ? null : latestCatalog.modules.find((m) => m.id === base?.id) ?? null;
    setBaseCatalog(latestCatalog);
    setConflict(null);
    if (!creating && !latest) {
      setProblems([{ code: 'missing', message: 'Someone removed this module. Close the editor and create it again if you still need it.' }]);
      return;
    }
    const pending: PendingMerge = { original: base, yours: draft, latest, catalog: latestCatalog, choices: {} };
    const merged = mergeModule(pending.original, pending.yours, pending.latest, pending.catalog);
    if (merged.conflicts.length === 0) applyMerge(merged, latest);
    else setPendingMerge(pending);
  };

  const discardMine = async (): Promise<void> => {
    const latestCatalog = await reloadCatalog();
    if (!latestCatalog) return;
    const latest = latestCatalog.modules.find((m) => m.id === base?.id) ?? null;
    setBaseCatalog(latestCatalog);
    setBase(latest);
    setDraft(draftFrom(latest));
    setConflict(null);
    setPendingMerge(null);
    setMergeNotes([]);
  };

  const impact = impactOf(draft, base, baseCatalog);
  const recovery = holdsRecoveryRoute(draft);
  const originalKeys = new Set(base?.items.map((i) => i.navKey) ?? []);

  const details = (
    <Form layout="vertical" requiredMark="optional" onFinish={() => void save()} disabled={saving}>
      {problems.length > 0 && (
        <Alert type="error" showIcon role="alert" style={{ marginBottom: 16 }} message="This module was not saved" description={<ul style={{ margin: 0, paddingLeft: 18 }}>{problems.map((p, i) => <li key={i}>{p.message}</li>)}</ul>} />
      )}
      {mergeNotes.length > 0 && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Your changes are now on top of the latest version — review, then save." description={<ul style={{ margin: 0, paddingLeft: 18 }}>{mergeNotes.map((n, i) => <li key={i}>{n}</li>)}</ul>} />
      )}

      <Section title="General">
        <Form.Item label="Name" required validateStatus={fieldErrors.title ? 'error' : undefined} help={fieldErrors.title || undefined}>
          <Input
            value={draft.title}
            maxLength={120}
            onChange={(e) => {
              set('title', e.target.value);
              if (creating && !keyTouched) set('moduleKey', slugify(e.target.value));
            }}
            placeholder="e.g. Clinical review"
            autoFocus
          />
        </Form.Item>
        <Form.Item
          label="Key"
          required
          validateStatus={fieldErrors.moduleKey ? 'error' : undefined}
          help={fieldErrors.moduleKey || (creating ? 'Lowercase letters, digits and hyphens. It never changes after creation.' : 'Keys never change, so links and history stay stable.')}
        >
          <Input
            value={draft.moduleKey}
            maxLength={80}
            disabled={!creating}
            onChange={(e) => {
              setKeyTouched(true);
              set('moduleKey', e.target.value);
            }}
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          />
        </Form.Item>
        <Form.Item label="Description" validateStatus={fieldErrors.description ? 'error' : undefined} help={fieldErrors.description || 'One line shown with the module in the launcher.'}>
          <Input.TextArea value={draft.description} maxLength={500} showCount autoSize={{ minRows: 2, maxRows: 4 }} onChange={(e) => set('description', e.target.value)} />
        </Form.Item>
        <Space wrap size={16} style={{ width: '100%' }}>
          <Form.Item label="Icon" required validateStatus={fieldErrors.iconKey ? 'error' : undefined} help={fieldErrors.iconKey || undefined} style={{ minWidth: 200 }}>
            <Select
              value={draft.iconKey}
              onChange={(v) => set('iconKey', v)}
              aria-label="Icon"
              options={(baseCatalog.iconKeys.length ? baseCatalog.iconKeys : Object.keys(MODULE_ICONS)).map((k) => ({
                value: k,
                label: (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {moduleGlyph(k)} {k}
                  </span>
                ),
              }))}
            />
          </Form.Item>
          <Form.Item label="Color" validateStatus={fieldErrors.color ? 'error' : undefined} help={fieldErrors.color || 'Hex, e.g. #4F46E5. Kept readable under a white glyph.'} style={{ minWidth: 200 }}>
            <Space.Compact>
              <input
                type="color"
                aria-label="Pick a color"
                value={/^#[0-9a-fA-F]{6}$/.test(draft.color) ? draft.color : '#64748B'}
                onChange={(e) => set('color', e.target.value.toUpperCase())}
                style={{ width: 40, height: 32, padding: 2, border: `1px solid ${colors.slate[300]}`, borderRadius: '6px 0 0 6px', background: 'transparent', cursor: 'pointer' }}
              />
              <Input value={draft.color} maxLength={7} onChange={(e) => set('color', e.target.value)} placeholder="#64748B" style={{ width: 120 }} aria-label="Color hex" />
            </Space.Compact>
          </Form.Item>
        </Space>
        <Form.Item
          label="Show in navigation"
          help={
            recovery
              ? 'This module holds Modules (this page), so it stays shown — otherwise there would be no way back here. Move Modules to another shown module first.'
              : 'Hiding removes the tile, the menu, search results and pins. It does not revoke access: anyone allowed can still open its pages from a bookmark. Access is managed separately.'
          }
        >
          <Switch
            checked={draft.isVisible}
            onChange={(v) => set('isVisible', v)}
            disabled={recovery && draft.isVisible}
            checkedChildren="Shown"
            unCheckedChildren="Hidden"
            aria-label="Show in navigation"
          />
        </Form.Item>
      </Section>

      <Section
        title={`Pages (${draft.items.length})`}
        extra={
          <Button icon={<PlusOutlined />} onClick={() => setPickerOpen(true)}>
            Add pages
          </Button>
        }
      >
        {draft.items.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No pages yet — this module is a draft and won't appear in the launcher." />
        ) : (
          <ol aria-label="Pages in this module, in order" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {draft.items.map((item, index) => {
              const d = destinationByKey(item.navKey);
              const movedIn = item.fromModuleKey !== (base?.moduleKey ?? null) && !originalKeys.has(item.navKey);
              const title = titleOf(item.navKey, baseCatalog);
              return (
                <li
                  key={item.navKey}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    marginBottom: 6,
                    borderRadius: 8,
                    border: `1px solid ${movedIn ? colors.amber[500] : isDark ? colors.slate[700] : colors.slate[200]}`,
                    background: movedIn ? (isDark ? colors.amber[700] + '33' : colors.amber[100]) : 'transparent',
                  }}
                >
                  <span aria-hidden style={{ width: 18, display: 'inline-flex', justifyContent: 'center', color: colors.slate[500] }}>{d?.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500 }}>
                      {title}
                      {draft.defaultNavKey === item.navKey && <Tag color="blue" style={{ marginLeft: 8 }}>Default</Tag>}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>{d?.to ?? item.navKey}</Text>
                    <span style={{ marginLeft: 8 }}><ScopeTag scope={d?.scope} /></span>
                    {movedIn && (
                      <div style={{ fontSize: 12, color: isDark ? colors.amber[100] : colors.amber[700], marginTop: 2 }}>
                        <SwapOutlined /> {item.fromModuleKey ? `Moves from ${baseCatalog.modules.find((m) => m.moduleKey === item.fromModuleKey)?.title ?? item.fromModuleKey}` : 'Was in no module'}
                      </div>
                    )}
                  </div>
                  <Space size={2}>
                    <Tooltip title="Move up">
                      <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => move(index, -1)} aria-label={`Move ${title} up`} />
                    </Tooltip>
                    <Tooltip title="Move down">
                      <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={index === draft.items.length - 1} onClick={() => move(index, 1)} aria-label={`Move ${title} down`} />
                    </Tooltip>
                    {originalKeys.has(item.navKey) ? (
                      <Tooltip title="A page leaves a module only by being added to another one.">
                        <Button size="small" type="text" icon={<LockOutlined />} disabled aria-label={`${title} stays until another module takes it`} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Don't add">
                        <Button size="small" type="text" icon={<DeleteOutlined />} onClick={() => removeAdded(item.navKey)} aria-label={`Don't add ${title}`} />
                      </Tooltip>
                    )}
                  </Space>
                </li>
              );
            })}
          </ol>
        )}

        <Form.Item label="Opens on" style={{ marginTop: 12, marginBottom: 0 }} help="Where the module's tile lands. If someone can't open this page, they land on the first page they can.">
          <Select
            value={draft.defaultNavKey ?? ''}
            onChange={(v) => set('defaultNavKey', v || null)}
            aria-label="Default page"
            disabled={draft.items.length === 0}
            options={[{ value: '', label: 'First page each person can open' }, ...draft.items.map((i) => ({ value: i.navKey, label: titleOf(i.navKey, baseCatalog) }))]}
          />
        </Form.Item>
      </Section>

      <Section title="Preview">
        <Preview draft={draft} isDark={isDark} catalog={baseCatalog} />
      </Section>

      {impact.length > 0 && (
        <Section title="What this save changes">
          <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
            {impact.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </Section>
      )}

      <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
        Modules organise pages that already exist. Creating or editing one never installs tools, enables clinical features or changes who may open a page.
      </Paragraph>
    </Form>
  );

  return (
    <Drawer
      open={open}
      onClose={requestClose}
      size={screens.md ? 720 : '100%'}
      title={creating ? 'New module' : `Edit "${base?.title ?? ''}"`}
      destroyOnHidden
      mask={{ closable: !dirty }}
      footer={
        tab === 'details' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={requestClose}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={() => void save()} disabled={!!conflict || !!pendingMerge || (!creating && !dirty)}>
              {creating ? 'Create module' : 'Save'}
            </Button>
          </div>
        )
      }
    >
      {conflict && (
        <ConflictPanel
          conflict={conflict}
          original={base}
          draft={draft}
          catalog={baseCatalog}
          onReapply={() => void mergeWithLatest()}
          onDiscard={() => void discardMine()}
        />
      )}
      {pendingMerge && (
        <MergePanel
          pending={pendingMerge}
          onChoose={(id, choice) => setPendingMerge((p) => (p ? { ...p, choices: { ...p.choices, [id]: choice } } : p))}
          onApply={(merged) => applyMerge(merged, pendingMerge.latest)}
          onDiscard={() => void discardMine()}
        />
      )}
      {creating ? (
        details
      ) : (
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            { key: 'details', label: 'Details', children: details },
            { key: 'history', label: 'History', children: base ? <ModuleHistory moduleId={base.id} catalog={baseCatalog} /> : null },
          ]}
        />
      )}

      <PagePickerDrawer
        open={pickerOpen}
        catalog={baseCatalog}
        selfKey={base?.moduleKey ?? null}
        assigned={new Set(draft.items.map((i) => i.navKey))}
        onClose={() => setPickerOpen(false)}
        onAdd={(keys) => {
          addItems(keys);
          setPickerOpen(false);
        }}
      />
    </Drawer>
  );
});

// ── Pieces ───────────────────────────────────────────────────────────────────

function Section({ title, extra, children }: { title: string; extra?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 24 }} aria-label={title}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: colors.slate[500] }}>{title}</h3>
        {extra}
      </div>
      {children}
    </section>
  );
}

export function ScopeTag({ scope }: { scope?: string | null }) {
  if (scope === 'platform') return <Tag icon={<GlobalOutlined />} color="purple">Platform</Tag>;
  if (scope === 'both') return <Tag icon={<GlobalOutlined />} color="purple">Platform + merchant</Tag>;
  if (scope === 'merchant') return <Tag icon={<ShopOutlined />}>Merchant</Tag>;
  return null;
}

function Preview({ draft, isDark, catalog }: { draft: ModuleDraft; isDark: boolean; catalog: StudioModuleCatalog }) {
  const landing = draft.defaultNavKey ?? draft.items[0]?.navKey;
  return (
    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: 120 }} aria-label="Launcher tile preview">
        <ModuleIcon glyph={moduleGlyph(draft.iconKey)} color={draft.color || undefined} />
        <span style={{ fontSize: 12.5, fontWeight: 600, textAlign: 'center' }}>{draft.title || 'Untitled'}</span>
        {!draft.isVisible && <Tag icon={<WarningOutlined />}>Hidden</Tag>}
        {draft.items.length === 0 && <Tag>Draft</Tag>}
      </div>
      <div
        aria-label="Menu preview"
        style={{ flex: 1, minWidth: 200, border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`, borderRadius: 10, padding: 10 }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: colors.slate[500], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{draft.title || 'Untitled'}</div>
        {draft.items.slice(0, 6).map((i) => (
          <div key={i.navKey} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 13 }}>
            <span aria-hidden style={{ color: colors.slate[500] }}>{destinationByKey(i.navKey)?.icon}</span>
            {titleOf(i.navKey, catalog)}
            {i.navKey === landing && <Text type="secondary" style={{ fontSize: 11 }}>· opens here</Text>}
          </div>
        ))}
        {draft.items.length > 6 && <Text type="secondary" style={{ fontSize: 12 }}>+ {draft.items.length - 6} more</Text>}
        {draft.items.length === 0 && <Text type="secondary">No pages</Text>}
      </div>
    </div>
  );
}

function ConflictPanel({
  conflict,
  original,
  draft,
  catalog,
  onReapply,
  onDiscard,
}: {
  conflict: { message: string; current: StudioModule | null };
  /** The module as this editor opened it (null when creating). */
  original: StudioModule | null;
  draft: ModuleDraft;
  catalog: StudioModuleCatalog;
  onReapply: () => void;
  onDiscard: () => void;
}) {
  const now = conflict.current;
  const opensOn = (key: string | null | undefined) => (key ? titleOf(key, catalog) : 'first page');
  const pages = (keys: string[]) => keys.map((k) => titleOf(k, catalog)).join(', ') || '—';
  const known = (m: StudioModule | null) => (m ? m.items.filter((i) => i.known).map((i) => i.navKey) : []);
  // [label, when you opened it, saved now, your edit]
  const rows: Array<[string, string, string, string]> = now
    ? [
        ['Name', original?.title ?? '—', now.title, draft.title],
        ['Description', original?.description || '—', now.description || '—', draft.description || '—'],
        ['Icon', original?.iconKey ?? '—', now.iconKey, draft.iconKey],
        ['Shown', original ? (original.isVisible ? 'Shown' : 'Hidden') : '—', now.isVisible ? 'Shown' : 'Hidden', draft.isVisible ? 'Shown' : 'Hidden'],
        ['Opens on', original ? opensOn(original.defaultNavKey) : '—', opensOn(now.defaultNavKey), opensOn(draft.defaultNavKey)],
        ['Pages', original ? pages(known(original)) : '—', pages(known(now)), pages(draft.items.map((i) => i.navKey))],
      ]
    : [];
  return (
    <Alert
      type="warning"
      showIcon
      role="alert"
      style={{ marginBottom: 16 }}
      message="Someone changed the modules while you were editing"
      description={
        <div>
          <Paragraph style={{ marginBottom: 8 }}>{conflict.message} Nothing of yours was saved.</Paragraph>
          {rows.length > 0 && (
            <table aria-label="Your edit beside the saved module" style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse', marginBottom: 10 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '2px 6px' }} />
                  <th style={{ textAlign: 'left', padding: '2px 6px' }}>When you opened it</th>
                  <th style={{ textAlign: 'left', padding: '2px 6px' }}>Saved now</th>
                  <th style={{ textAlign: 'left', padding: '2px 6px' }}>Your edit</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([label, before, saved, mine]) => (
                  <tr key={label} style={{ background: saved !== mine ? 'rgba(217,119,6,0.08)' : undefined }}>
                    <th scope="row" style={{ textAlign: 'left', padding: '2px 6px', fontWeight: 600 }}>{label}</th>
                    <td style={{ padding: '2px 6px' }}>{before}</td>
                    <td style={{ padding: '2px 6px' }}>{saved}</td>
                    <td style={{ padding: '2px 6px' }}>{mine}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Space wrap>
            <Button type="primary" onClick={onReapply}>Load theirs and keep my changes</Button>
            <Button onClick={onDiscard}>Discard my changes</Button>
          </Space>
        </div>
      }
    />
  );
}

/** Both sides changed the same thing: show Original / Yours / Current for each and wait for a choice on every one. */
function MergePanel({
  pending,
  onChoose,
  onApply,
  onDiscard,
}: {
  pending: PendingMerge;
  onChoose: (id: string, choice: MergeChoice) => void;
  onApply: (merged: ModuleMerge) => void;
  onDiscard: () => void;
}) {
  const merged = mergeModule(pending.original, pending.yours, pending.latest, pending.catalog, pending.choices);
  return (
    <Alert
      type="warning"
      showIcon
      role="alert"
      style={{ marginBottom: 16 }}
      message={`You and someone else both changed ${merged.conflicts.length === 1 ? 'one thing' : `${merged.conflicts.length} things`} — choose which to keep`}
      description={
        <div>
          <Paragraph style={{ marginBottom: 8 }}>Everything else is merged: their changes are kept where you didn't touch them, and yours where they didn't.</Paragraph>
          {merged.conflicts.map((c) => (
            <section key={c.id} aria-label={`Choose: ${c.label}`} style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>{c.label}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>When you opened it: {c.original}</Text>
              <Radio.Group
                value={pending.choices[c.id]}
                onChange={(e) => onChoose(c.id, e.target.value as MergeChoice)}
                style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}
              >
                <Radio value="yours">Keep yours: {c.yours}</Radio>
                <Radio value="current">Keep theirs: {c.current}</Radio>
              </Radio.Group>
            </section>
          ))}
          <Space wrap>
            <Button type="primary" disabled={merged.unresolved.length > 0} onClick={() => onApply(merged)}>
              {merged.unresolved.length > 0 ? `Choose ${merged.unresolved.length} more` : 'Use these choices'}
            </Button>
            <Button onClick={onDiscard}>Discard my changes</Button>
          </Space>
        </div>
      }
    />
  );
}

function ModuleHistory({ moduleId, catalog }: { moduleId: number; catalog: StudioModuleCatalog }) {
  const [rows, setRows] = useState<StudioModuleChange[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getModuleHistory(moduleId, 100)
      .then((r) => !cancelled && setRows(r))
      .catch((e: Error) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [moduleId]);
  if (error) return <Alert type="error" showIcon message="Couldn't load the history" description={error} />;
  if (!rows) return <Spin />;
  if (rows.length === 0) return <Empty description="No changes recorded yet." />;
  return (
    <Timeline
      items={rows.map((r) => ({
        key: r.id,
        children: (
          <div>
            <div>
              <Tag>{r.action}</Tag>
              <Text strong>{r.actorName ?? `Account ${r.actorId}`}</Text>{' '}
              <Text type="secondary">{dayjs(r.atUtc).format('D MMM YYYY, HH:mm')}</Text>
            </div>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {describeChange(r, catalog).map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </div>
        ),
      }))}
    />
  );
}

function PagePickerDrawer({
  open,
  catalog,
  selfKey,
  assigned,
  onClose,
  onAdd,
}: {
  open: boolean;
  catalog: StudioModuleCatalog;
  selfKey: string | null;
  assigned: Set<string>;
  onClose: () => void;
  onAdd: (keys: string[]) => void;
}) {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const screens = Grid.useBreakpoint();
  useEffect(() => {
    if (open) {
      setQuery('');
      setPicked([]);
    }
  }, [open]);

  const q = query.trim().toLowerCase();
  const candidates = catalog.destinations
    .filter((d) => !assigned.has(d.navKey))
    .filter((d) => !q || `${d.title} ${d.navKey} ${d.route} ${d.ownerModuleKey ?? ''}`.toLowerCase().includes(q));

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size={screens.sm ? 520 : '100%'}
      title="Add pages"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" disabled={picked.length === 0} onClick={() => onAdd(picked)}>
            Add {picked.length || ''}
          </Button>
        </div>
      }
    >
      <Paragraph type="secondary" style={{ fontSize: 12.5 }}>
        Only pages Agent Studio already has are listed. Adding one moves it from its current module; its URL, data scope and who may open it stay the same.
      </Paragraph>
      <Input.Search placeholder="Search pages" value={query} onChange={(e) => setQuery(e.target.value)} allowClear autoFocus aria-label="Search pages" style={{ marginBottom: 12 }} />
      <List
        dataSource={candidates}
        locale={{ emptyText: 'Every page is already in this module.' }}
        renderItem={(d) => {
          const supported = !!destinationByKey(d.navKey);
          const owner = catalog.modules.find((m) => m.moduleKey === d.ownerModuleKey);
          const checked = picked.includes(d.navKey);
          return (
            <List.Item style={{ paddingLeft: 0, paddingRight: 0 }}>
              <Checkbox
                checked={checked}
                disabled={!supported}
                onChange={(e) => setPicked((p) => (e.target.checked ? [...p, d.navKey] : p.filter((k) => k !== d.navKey)))}
                style={{ width: '100%' }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span aria-hidden>{destinationByKey(d.navKey)?.icon}</span> {d.title}
                  </span>
                  <span style={{ fontSize: 12 }}>
                    <Text type="secondary" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{d.route}</Text>
                    <span style={{ marginLeft: 6 }}><ScopeTag scope={d.scope} /></span>
                    {d.access !== 'studio' && <Tag icon={<LockOutlined />}>{d.access === 'admin' ? 'Admins' : `Needs ${d.claim}`}</Tag>}
                  </span>
                  <Text type={owner && owner.moduleKey !== selfKey ? 'warning' : 'secondary'} style={{ fontSize: 12 }}>
                    {!supported ? "This version of Agent Studio can't open it yet." : owner ? `Now in ${owner.title} — adding moves it here` : 'In no module yet'}
                    {owner?.defaultNavKey === d.navKey ? ` (it is ${owner.title}'s default)` : ''}
                  </Text>
                </div>
              </Checkbox>
            </List.Item>
          );
        }}
      />
    </Drawer>
  );
}
